import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import Model, { Providers } from "@tripian/model";
import { providers } from "@tripian/core";
import { saveNotification } from "../redux/action/user";
import { sanitizeLivnErrorMessage } from "../utils/livnValidation";

/**
 * Livn's error bodies carry raw server detail — a 500 from previewPrice, for example, reads
 * "Unable to generate price preview due to: com.livnapi...InternalErrorException: 500
 * INTERNAL_SERVER_ERROR ...". These strings end up in user-facing notifications, so they are
 * sanitised before they ever reach the store.
 */
const livnNotificationText = (error: any): string =>
  sanitizeLivnErrorMessage(error?.customerErrorMessage ?? error?.message);

const useLivnApi = () => {
  const [loadingLivnProductInfo, setLoadingLivnProductInfo] = useState<boolean>(false);
  const [loadingLivnDepartures, setLoadingLivnDepartures] = useState<boolean>(false);
  const [loadingLivnFlow, setLoadingLivnFlow] = useState<boolean>(false);
  const [loadingLivnPricePreview, setLoadingLivnPricePreview] = useState<boolean>(false);

  const [loadingLivnBookings, setLoadingLivnBookings] = useState<boolean>(false);

  const [livnProductInfo, setLivnProductInfo] = useState<Providers.Livn.ProductDetails>();
  const [livnDepartures, setLivnDepartures] = useState<Providers.Livn.DepartureDate[]>([]);
  const [livnFlow, setLivnFlow] = useState<Providers.Livn.Flow>();
  const [livnPricePreview, setLivnPricePreview] = useState<Providers.Livn.PricePreview>();
  const [livnBookings, setLivnBookings] = useState<Providers.Livn.Booking[]>();

  const dispatch = useDispatch();

  const fetchLivnProductInfo = useCallback(
    async (productId: number): Promise<Providers.Livn.ProductDetails | null> => {
      if (!providers.livn || !window.tconfig.SHOW_TOURS_AND_TICKETS) return null;

      setLoadingLivnProductInfo(true);
      setLivnProductInfo(undefined);

      try {
        const data = await providers.livn.productDetails(productId);
        setLivnProductInfo(data);
        return data;
      } catch (error: any) {
        dispatch(
          saveNotification(Model.NOTIFICATION_TYPE.ERROR, "livnFetchProductInfoError", livnNotificationText(error))
        );
        return null;
      } finally {
        setLoadingLivnProductInfo(false);
      }
    },
    [dispatch]
  );

  const fetchLivnDepartures = useCallback(
    async (
      productId: number,
      startDate: string,
      endDate: string
    ): Promise<Providers.Livn.DepartureDate[] | null> => {
      if (!providers.livn || !window.tconfig.SHOW_TOURS_AND_TICKETS) return null;

      setLoadingLivnDepartures(true);
      setLivnDepartures([]);

      try {
        const data = await providers.livn.productDepartures(productId, startDate, endDate);
        setLivnDepartures(data.departures || []);
        return data.departures || [];
      } catch (error: any) {
        dispatch(
          saveNotification(Model.NOTIFICATION_TYPE.ERROR, "livnFetchDeparturesError", livnNotificationText(error))
        );
        return null;
      } finally {
        setLoadingLivnDepartures(false);
      }
    },
    [dispatch]
  );

  const startLivnFlow = useCallback(
    async (productId: number, date: string, currency?: string): Promise<Providers.Livn.Flow | null> => {
      if (!providers.livn || !window.tconfig.SHOW_TOURS_AND_TICKETS) return null;

      setLoadingLivnFlow(true);
      setLivnFlow(undefined);
      setLivnPricePreview(undefined);
      setLivnBookings(undefined);

      try {
        const flow = await providers.livn.startFlow(productId, date, currency);
        setLivnFlow(flow);
        return flow;
      } catch (error: any) {
        dispatch(saveNotification(Model.NOTIFICATION_TYPE.ERROR, "livnStartFlowError", livnNotificationText(error)));
        return null;
      } finally {
        setLoadingLivnFlow(false);
      }
    },
    [dispatch]
  );

  const continueLivnFlow = useCallback(
    async (flowData: Providers.Livn.FlowRequest): Promise<Providers.Livn.Flow | null> => {
      if (!providers.livn || !window.tconfig.SHOW_TOURS_AND_TICKETS) return null;

      setLoadingLivnFlow(true);

      try {
        const flow = await providers.livn.continueFlow(flowData);
        setLivnFlow(flow);
        setLivnPricePreview(undefined);
        return flow;
      } catch (error: any) {
        dispatch(
          saveNotification(Model.NOTIFICATION_TYPE.ERROR, "livnContinueFlowError", livnNotificationText(error))
        );
        return null;
      } finally {
        setLoadingLivnFlow(false);
      }
    },
    [dispatch]
  );

  const goBackLivnFlow = useCallback(
    async (flowId: number): Promise<Providers.Livn.Flow | null> => {
      if (!providers.livn || !window.tconfig.SHOW_TOURS_AND_TICKETS) return null;

      setLoadingLivnFlow(true);

      try {
        const flow = await providers.livn.goBackFlow(flowId);
        setLivnFlow(flow);
        setLivnPricePreview(undefined);
        return flow;
      } catch (error: any) {
        dispatch(
          saveNotification(Model.NOTIFICATION_TYPE.ERROR, "livnGoBackFlowError", livnNotificationText(error))
        );
        return null;
      } finally {
        setLoadingLivnFlow(false);
      }
    },
    [dispatch]
  );

  const fetchLivnPricePreview = useCallback(
    async (flowData: Providers.Livn.FlowRequest): Promise<Providers.Livn.PricePreview | null> => {
      if (!providers.livn || !window.tconfig.SHOW_TOURS_AND_TICKETS) return null;

      setLoadingLivnPricePreview(true);
      setLivnPricePreview(undefined);

      try {
        const preview = await providers.livn.previewPrice(flowData);
        setLivnPricePreview(preview);
        return preview;
      } catch (error: any) {
        dispatch(
          saveNotification(Model.NOTIFICATION_TYPE.ERROR, "livnPricePreviewError", livnNotificationText(error))
        );
        return null;
      } finally {
        setLoadingLivnPricePreview(false);
      }
    },
    [dispatch]
  );

  const fetchLivnBookings = useCallback(
    async (anyReference: string): Promise<Providers.Livn.Booking[] | null> => {
      if (!providers.livn || !window.tconfig.SHOW_TOURS_AND_TICKETS) return null;

      setLoadingLivnBookings(true);
      try {
        const bookings = await providers.livn.getBookings(anyReference);
        setLivnBookings(bookings);
        return bookings;
      } catch (error: any) {
        dispatch(
          saveNotification(Model.NOTIFICATION_TYPE.ERROR, "livnFetchBookingsError", livnNotificationText(error))
        );
        return null;
      } finally {
        setLoadingLivnBookings(false);
      }
    },
    [dispatch]
  );

  return {
    fetchLivnProductInfo,
    fetchLivnDepartures,
    startLivnFlow,
    continueLivnFlow,
    goBackLivnFlow,
    fetchLivnPricePreview,
    fetchLivnBookings,
    loadingLivnProductInfo,
    loadingLivnDepartures,
    loadingLivnFlow,
    loadingLivnPricePreview,
    loadingLivnBookings,
    livnProductInfo,
    livnDepartures,
    livnFlow,
    livnPricePreview,
    livnBookings,
    setLivnBookings,
    setLivnPricePreview,
  };
};

export default useLivnApi;
